/** The platform's cut — set per product category, and per service module. */

export type Vertical = 'FOOD' | 'PHARMACY';
export type CommissionMode = 'RATE' | 'FLAT';

export type CommissionRule = {
  id: string;
  vertical: Vertical;
  category: string;
  mode: CommissionMode;
  rate_pct: number | null;
  amount_inr: number | null;
  is_active: boolean;
  updated_at: string;
};

export type ServiceCommissionRule = {
  id: string;
  module_type: string;
  mode: CommissionMode;
  rate_pct: number | null;
  amount_inr: number | null;
  is_active: boolean;
  updated_at: string;
};

export type SetRuleInput = {
  vertical: Vertical;
  category: string;
  mode: CommissionMode;
  rate_pct?: number;
  amount_inr?: number;
  is_active?: boolean;
};

export type SetServiceRuleInput = {
  module_type: string;
  mode: CommissionMode;
  rate_pct?: number;
  amount_inr?: number;
  is_active?: boolean;
};

/** Mirrors the backend enums — the two verticals do not share a category list. */
export const FOOD_CATEGORIES = ['DRY_FOOD', 'WET_FOOD', 'TREATS', 'SUPPLEMENT', 'TOYS', 'OTHER'] as const;
export const PHARMACY_CATEGORIES = [
  'MEDICINE',
  'SUPPLEMENT',
  'PRESCRIPTION_DIET',
  'GROOMING',
  'WELLNESS',
  'OTHER',
] as const;

export const categories_for = (v: Vertical): readonly string[] =>
  v === 'FOOD' ? FOOD_CATEGORIES : PHARMACY_CATEGORIES;

/** Only the modules that are booked as services — the commerce verticals use categories. */
export const SERVICE_MODULES = [
  'VET',
  'WALKER',
  'TRAINER',
  'GROOMER',
  'LAB',
  'CLINIC',
  'CREMATION',
  'CAFE',
  'RESTAURANT',
] as const;

export const pretty = (s: string): string =>
  s
    .split('_')
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ');

/** A rule as one readable phrase — "10%" or "₹40 per unit". */
export function rule_amount(r: { mode: CommissionMode; rate_pct: number | null; amount_inr: number | null }): string {
  return r.mode === 'FLAT' ? `₹${r.amount_inr ?? 0}` : `${r.rate_pct ?? 0}%`;
}

/**
 * A worked example, because the direction is the thing people get wrong.
 *
 * Goods add the fee on top of the vendor's price; services deduct it from what the
 * customer pays. Showing ₹500 run through the rule is the cheapest way to stop an
 * admin setting 10% and expecting the other one.
 */
export function goods_example(r: { mode: CommissionMode; rate_pct: number | null; amount_inr: number | null }): string {
  const fee = r.mode === 'FLAT' ? (r.amount_inr ?? 0) : (500 * (r.rate_pct ?? 0)) / 100;
  return `Vendor lists ₹500 → customer pays ₹${500 + fee}, vendor keeps ₹500`;
}

export function service_example(r: {
  mode: CommissionMode;
  rate_pct: number | null;
  amount_inr: number | null;
}): string {
  const fee = Math.min(r.mode === 'FLAT' ? (r.amount_inr ?? 0) : (500 * (r.rate_pct ?? 0)) / 100, 500);
  return `Customer pays ₹500 → vendor takes home ₹${500 - fee}`;
}

/* ------------------------------- coverage -------------------------------- */

/**
 * What a rule is worth, and where there is none.
 *
 * The old screen could set a rate but say nothing about its consequences: a 10% rule on a
 * category nobody sells in and a 10% rule on half the shop rendered identically, and a
 * category with NO rule — where the vendor keeps the entire sale — was a grey line of
 * text among eleven that looked the same.
 */
export type GoodsCoverage = {
  vertical: Vertical;
  category: string;
  /** The admin's own name for it — this screen no longer de-slugs the key into words. */
  label: string;
  /**
   * False for a retired category. Its rule is still listed, because products already
   * filed under it are still selling and still being charged.
   */
  is_active: boolean;
  rule: CommissionRule | null;
  /** Curated masters in this category. */
  masters: number;
  /** Live vendor listings the rule would apply to. */
  listings: number;
  /** Of those, how many carry their own fee and therefore ignore the rule. */
  overrides: number;
  /** Listings the rule actually governs today. */
  governed: number;
};

export type ServiceCoverage = {
  module_type: string;
  rule: ServiceCommissionRule | null;
  /** Verified vendors offering this module. */
  vendors: number;
};

export type CoverageSummary = {
  goods_categories: number;
  goods_priced: number;
  listings_total: number;
  /** Live listings whose category has no active rule — the platform earns nothing on these. */
  listings_uncovered: number;
  product_overrides: number;
  variant_overrides: number;
  service_modules: number;
  services_priced: number;
  /** Verified vendors working in a module with no active rule. */
  vendors_unpriced: number;
};

export type Coverage = {
  goods: GoodsCoverage[];
  services: ServiceCoverage[];
  summary: CoverageSummary;
};

export const VERTICAL_LABEL: Record<Vertical, string> = {
  FOOD: 'Food & products',
  PHARMACY: 'Medicines',
};

/**
 * A rule's current state, as one number and one short line under it.
 *
 * Deliberately not a status pill. A pill has to hold a phrase, a phrase has to fit a
 * column, and "Vendors keep everything" in a column narrow enough to sit beside a
 * percentage truncates to "Vendors ke…" — which says nothing at all. The amount is the
 * thing being read here, so it gets the size, and the consequence goes underneath in
 * plain words where it has room to be a sentence.
 */
export type FeeTone = 'default' | 'muted' | 'warning' | 'destructive';

export type FeeDisplay = { amount: string; note: string; tone: FeeTone };

export function fee_display(
  rule: { mode: CommissionMode; rate_pct: number | null; amount_inr: number | null; is_active: boolean } | null,
  /**
   * Whether an unset rule is actually costing anything.
   *
   * A category with no rule AND nothing on sale in it is not a leak, and rendering eight
   * of those in alarm red teaches an operator to ignore the colour by the time they reach
   * the one that matters.
   */
  has_reach: boolean,
): FeeDisplay {
  if (!rule) {
    return has_reach
      ? { amount: 'Nothing', note: 'vendors keep it all', tone: 'destructive' }
      : { amount: 'Nothing', note: 'no rule set', tone: 'muted' };
  }
  if (!rule.is_active) {
    return { amount: rule_amount(rule), note: 'paused — charging nothing', tone: 'warning' };
  }
  return {
    amount: rule_amount(rule),
    note: rule.mode === 'FLAT' ? 'per sale' : 'of every sale',
    tone: 'default',
  };
}

export const FEE_TONE_CLASS: Record<FeeTone, string> = {
  default: 'text-foreground',
  muted: 'text-muted-foreground',
  warning: 'text-warning',
  destructive: 'text-destructive',
};
